import { useState } from 'react';
import { RiCheckLine } from 'react-icons/ri';
import { Button } from '@/components/ui';
import type { ProofSchemaField } from '@/types/worker';
import styles from './ProofSchemaForm.module.css';

interface ProofSchemaFormProps {
    fields: ProofSchemaField[];
    onSubmit: (result: Record<string, unknown>) => void;
    isLoading: boolean;
    disabled?: boolean;
}

export function ProofSchemaForm({ fields, onSubmit, isLoading, disabled }: ProofSchemaFormProps) {
    const [values, setValues] = useState<Record<string, string>>(() => {
        const initial: Record<string, string> = {};
        fields.forEach((f) => { initial[f.name] = ''; });
        return initial;
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [showConfirm, setShowConfirm] = useState(false);

    const handleChange = (name: string, value: string) => {
        setValues((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        fields.forEach((field) => {
            const val = values[field.name]?.trim() || '';
            if (field.required && !val) {
                newErrors[field.name] = 'Este campo es obligatorio';
            } else if (field.min_length && val.length > 0 && val.length < field.min_length) {
                newErrors[field.name] = `Minimo ${field.min_length} caracteres (tienes ${val.length})`;
            }
            if (field.type === 'url' && val) {
                try {
                    new URL(val);
                } catch {
                    newErrors[field.name] = 'URL invalida';
                }
            }
            if (field.type === 'number' && val && isNaN(Number(val))) {
                newErrors[field.name] = 'Debe ser un numero';
            }
        });

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return false;
        }
        return true;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;
        setShowConfirm(true);
    };

    const handleConfirm = () => {
        const result: Record<string, unknown> = {};
        fields.forEach((field) => {
            const val = values[field.name];
            result[field.name] = field.type === 'number' ? Number(val) : val;
        });
        onSubmit(result);
        setShowConfirm(false);
    };

    const filledCount = fields.filter((f) => values[f.name]?.trim()).length;

    return (
        <form className={styles.form} onSubmit={handleSubmit}>
            {fields.map((field) => (
                <div key={field.name} className={styles.field}>
                    <label className={styles.label}>
                        {field.label}
                        {field.required && <span className={styles.required}>*</span>}
                    </label>

                    {field.type === 'select' && field.options ? (
                        <div className={styles.optionGroup}>
                            {field.options.map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    className={`${styles.optionCard} ${values[field.name] === opt.value ? styles.optionSelected : ''}`}
                                    onClick={() => handleChange(field.name, opt.value)}
                                    disabled={disabled}
                                >
                                    <span className={styles.optionRadio}>
                                        {values[field.name] === opt.value && <RiCheckLine />}
                                    </span>
                                    <span className={styles.optionLabel}>{opt.label}</span>
                                </button>
                            ))}
                            {errors[field.name] && (
                                <span className={styles.errorText}>{errors[field.name]}</span>
                            )}
                        </div>
                    ) : field.type === 'textarea' ? (
                        <>
                            <textarea
                                className={`${styles.textarea} ${errors[field.name] ? styles.inputError : ''}`}
                                value={values[field.name]}
                                onChange={(e) => handleChange(field.name, e.target.value)}
                                disabled={disabled}
                                rows={4}
                                placeholder={field.placeholder || `Escribe ${field.label.toLowerCase()}...`}
                            />
                            <div className={styles.fieldFooter}>
                                {errors[field.name] && (
                                    <span className={styles.errorText}>{errors[field.name]}</span>
                                )}
                                {field.min_length && (
                                    <span className={`${styles.charCount} ${(values[field.name]?.length || 0) >= field.min_length ? styles.charCountOk : ''}`}>
                                        {values[field.name]?.length || 0}/{field.min_length} min
                                    </span>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <input
                                type={field.type === 'number' ? 'number' : field.type === 'url' ? 'url' : 'text'}
                                className={`${styles.input} ${errors[field.name] ? styles.inputError : ''}`}
                                value={values[field.name]}
                                onChange={(e) => handleChange(field.name, e.target.value)}
                                disabled={disabled}
                                placeholder={field.type === 'url' ? 'https://...' : ''}
                            />
                            {errors[field.name] && (
                                <span className={styles.errorText}>{errors[field.name]}</span>
                            )}
                        </>
                    )}
                </div>
            ))}

            <div className={styles.progress}>
                <span className={styles.progressText}>
                    {filledCount} de {fields.length} campos completados
                </span>
                <div className={styles.progressBar}>
                    <div
                        className={styles.progressFill}
                        style={{ width: `${(filledCount / fields.length) * 100}%` }}
                    />
                </div>
            </div>

            {!showConfirm ? (
                <Button
                    type="submit"
                    variant="primary"
                    disabled={isLoading || disabled}
                >
                    Revisar y enviar
                </Button>
            ) : (
                <div className={styles.confirmBox}>
                    <p className={styles.confirmText}>
                        ¿Confirmas que deseas enviar tu trabajo? Esta accion no se puede deshacer.
                    </p>
                    <div className={styles.confirmActions}>
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => setShowConfirm(false)}
                            disabled={isLoading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="button"
                            variant="primary"
                            onClick={handleConfirm}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Enviando...' : 'Confirmar envio'}
                        </Button>
                    </div>
                </div>
            )}
        </form>
    );
}
